import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import PageLayout from "./PageLayout";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import usePageMeta from "../hooks/usePageMeta";
import Iridescence from "./Iridescence";
import { chatFlows } from "../data/chatFlows";
import { IRIDESCENCE_DARK, IRIDESCENCE_LIGHT } from "../lib/iridescence";

const MemoIridescence = React.memo(Iridescence);

const ChatMessage = ({ role, content }) => {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} ${isUser ? "" : "first:pt-4"}`}>
      <div
        className={`max-w-[80%] rounded-md px-4 py-3 text-base sm:text-lg shadow-lg ${
          isUser
            ? "bg-gradient-to-r from-blue-600 to-cyan-600 text-white dark:bg-gradient-to-r dark:from-[#ff3b1f] dark:to-[#e2361c] dark:text-white"
            : "bg-white/90 text-gray-900 dark:bg-[#1a1a1a] dark:text-white"
        }`}
      >
        {content}
      </div>
    </div>
  );
};

const ChatWindow = ({
  messages,
  isLoading,
  options,
  onSelectOption,
  disabled,
  showCta,
  ctaHref,
  ctaLabel,
}) => {
  const containerRef = useRef(null);
  const lastAssistantRef = useRef(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const rafId = requestAnimationFrame(() => {
      const target = lastAssistantRef.current;
      if (target) {
        const top = target.offsetTop - el.offsetTop;
        el.scrollTop = Math.max(top, 0);
      } else {
        el.scrollTop = el.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [messages, options, isLoading]);

  const lastAssistantIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === "assistant") {
        return i;
      }
    }
    return -1;
  })();

  return (
    <div
      ref={containerRef}
      className="flex w-full flex-1 min-h-0 flex-col gap-4 overflow-y-auto rounded-md bg-white/70 dark:bg-[#0f0f0f] p-4 sm:p-6 shadow-inner"
    >
      {messages.map((message, index) => {
        const isLastAssistant = message.role === "assistant" && index === lastAssistantIndex;
        return (
          <div key={`${message.role}-${index}`} ref={isLastAssistant ? lastAssistantRef : null}>
            <ChatMessage role={message.role} content={message.content} />
          </div>
        );
      })}
      {options && options.length > 0 && (
        <div className="mt-2 space-y-2">
          <div className="grid gap-1.5">
            {options.map((option) => (
              <GuidedOptionButton
                key={option.value}
                onClick={() => onSelectOption(option)}
                disabled={disabled}
              >
                {option.label}
              </GuidedOptionButton>
            ))}
          </div>
          <div className="h-4"></div>
          <div className="h-6"></div>
        </div>
      )}
      {showCta && ctaHref && ctaLabel && (
        <div className="mt-4 space-y-3">
          <div className="h-2"></div>
          <a
            href={ctaHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center rounded-md px-5 py-2 text-sm sm:text-base font-semibold bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-[#2a2a2a] dark:text-gray-100 dark:hover:bg-[#3a3a3a] shadow-lg"
          >
            {ctaLabel}
          </a>
          <div className="h-8"></div>
        </div>
      )}
      {isLoading && (
        <div className="flex justify-start">
          <div className="rounded-md bg-white/90 dark:bg-[#101010] px-4 py-3 text-sm text-gray-600 dark:text-gray-300 shadow-lg flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse"></span>
            Escribiendo...
          </div>
        </div>
      )}
    </div>
  );
};

const GuidedOptionButton = ({ children, onClick, selected, disabled }) => (
  <Button
    type="button"
    variant={selected ? "default" : "secondary"}
    onClick={onClick}
    disabled={disabled}
    className={`w-full justify-between rounded-md px-4 sm:px-5 py-7 text-left text-sm sm:text-base font-semibold transition-all duration-200 hover:scale-[1.01] border ${
      selected
        ? "bg-primary text-primary-foreground border-transparent shadow-lg"
        : "bg-card text-foreground border-border shadow-md hover:shadow-lg"
    }`}
  >
    <span>{children}</span>
  </Button>
);

const INITIAL_MESSAGES = [
  {
    role: "assistant",
    content: "Contame sobre qué tema de hardware o tecnología querés aprender y te guío paso a paso.",
  },
];

const buildFlowSummary = (flow, answers) => {
  const lines = flow.steps.map((step) => {
    const selected = step.options.find((opt) => opt.value === answers[step.id]);
    const answerText = selected ? selected.label : "Sin respuesta";
    return `${step.question} Respuesta: ${answerText}.`;
  });
  const nivel = answers.nivel ? ` Nivel solicitado: ${answers.nivel}.` : "";
  return `Tema: ${flow.label}.${nivel} Resumen: ${lines.join(" ")}`;
};

const ChatPage = () => {
  const { theme } = useTheme();
  const lightIridescence = IRIDESCENCE_LIGHT;
  const darkIridescence = IRIDESCENCE_DARK;
  usePageMeta({
    title: "Asistente educativo | RG Servicio Técnico",
    description:
      "Chat educativo sobre rendimiento, comparativas y tecnologías. Respuestas simples en español.",
    canonical: "https://rgserviciotecnico.com/ayuda",
  });

  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const buildMessagesFromState = (flow, nextAnswers, stepIndex) => {
    const base = [
      {
        role: "assistant",
        content: "Elegí un tema y respondé unas preguntas para recibir una recomendación clara.",
      },
      { role: "user", content: flow.label },
      { role: "assistant", content: flow.steps[0].question },
    ];

    const messagesAcc = [...base];
    for (let i = 0; i < stepIndex; i += 1) {
      const step = flow.steps[i];
      const selected = step.options.find((opt) => opt.value === nextAnswers[step.id]);
      if (!selected) break;
      messagesAcc.push({ role: "user", content: selected.label });
      const nextStep = flow.steps[i + 1];
      if (nextStep) {
        messagesAcc.push({ role: "assistant", content: nextStep.question });
      }
    }
    return messagesAcc;
  };

  const selectedFlow = useMemo(
    () => chatFlows.find((flow) => flow.id === selectedFlowId),
    [selectedFlowId]
  );

  const totalSteps = selectedFlow ? selectedFlow.steps.length : 0;
  const currentStep = selectedFlow ? selectedFlow.steps[currentStepIndex] : null;
  const optionChoices = useMemo(() => {
    if (!selectedFlow) {
      return chatFlows.map((flow) => ({ value: flow.id, label: flow.label }));
    }
    if (!isComplete && currentStep) {
      return currentStep.options;
    }
    return [];
  }, [selectedFlow, isComplete, currentStep]);

  const buildFallbackNotice = (reason) => {
    if (reason === "missing_api_key") {
      return "IA no conectada: falta configurar OPENROUTER_API_KEY en Netlify o en Netlify Dev.";
    }
    if (reason === "timeout") {
      return "IA no respondió a tiempo. Mostrando respuesta local.";
    }
    if (reason === "upstream_error") {
      return "IA no disponible en este momento. Mostrando respuesta local.";
    }
    return "IA no conectada. Mostrando respuesta local.";
  };

  const resetFlow = () => {
    setSelectedFlowId(null);
    setCurrentStepIndex(0);
    setAnswers({});
    setIsComplete(false);
    setNotice("");
    setError("");
    setMessages(INITIAL_MESSAGES);
  };

  const submitGuided = async (flow, nextAnswers, updatedMessages) => {
    setIsLoading(true);
    setError("");
    setNotice("");

    const localReply = flow.buildLocal(nextAnswers);
    setMessages([...updatedMessages, { role: "assistant", content: localReply }]);
    setIsLoading(false);
  };

  const handleSelectFlow = (flowId) => {
    const flow = chatFlows.find((item) => item.id === flowId);
    if (!flow) return;

    setMessages((prev) => [
      ...prev,
      { role: "user", content: flow.label },
      { role: "assistant", content: flow.steps[0].question },
    ]);
    setSelectedFlowId(flowId);
    setCurrentStepIndex(0);
    setAnswers({});
    setIsComplete(false);
    setNotice("");
    setError("");
  };

  const handleOptionSelect = (option) => {
    if (!selectedFlow || !currentStep) return;

    const nextAnswers = { ...answers, [currentStep.id]: option.value };
    const isLastStep = currentStepIndex >= totalSteps - 1;

    let newMessages = [...messages, { role: "user", content: option.label }];

    if (!isLastStep) {
      const nextQuestion = selectedFlow.steps[currentStepIndex + 1].question;
      newMessages = [...newMessages, { role: "assistant", content: nextQuestion }];
      setMessages(newMessages);
      setAnswers(nextAnswers);
      setCurrentStepIndex((prev) => prev + 1);
      return;
    }

    setMessages(newMessages);
    setAnswers(nextAnswers);
    setIsComplete(true);
    submitGuided(selectedFlow, nextAnswers, newMessages);
  };

  const handleBackStep = () => {
    if (!selectedFlow) return;
    if (!isComplete && currentStepIndex === 0) {
      resetFlow();
      return;
    }
    const lastIndex = selectedFlow.steps.length - 1;
    const targetIndex = isComplete ? lastIndex : currentStepIndex - 1;
    if (targetIndex < 0) return;
    const prevStep = selectedFlow.steps[targetIndex];
    const nextAnswers = { ...answers };
    if (prevStep) {
      delete nextAnswers[prevStep.id];
    }
    const rebuilt = buildMessagesFromState(selectedFlow, nextAnswers, targetIndex);
    setAnswers(nextAnswers);
    setMessages(rebuilt);
    setCurrentStepIndex(targetIndex);
    setIsComplete(false);
  };

  return (
    <PageLayout
      className="relative bg-gradient-to-br from-[#f9fbff] via-white to-[#eef7ff] dark:from-[#07070b] dark:via-[#120b14] dark:to-[#2a0f0f]"
      before={
        <>
          <div className="absolute inset-0 opacity-28 pointer-events-none dark:hidden" key={`light-${theme}`}>
            <MemoIridescence color={lightIridescence} speed={0.55} amplitude={0.12} />
          </div>
          <div className="absolute inset-0 opacity-18 pointer-events-none hidden dark:block" key={`dark-${theme}`}>
            <MemoIridescence color={darkIridescence} speed={0.55} amplitude={0.1} />
          </div>
        </>
      }
    >
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <Card className="shadow-2xl rounded-md p-6 sm:p-8 backdrop-blur h-[700px] lg:h-[600px] flex flex-col">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-muted-foreground">
                  Asistente educativo
                </p>
                <h1 className="mt-3 text-2xl sm:text-3xl font-bold text-foreground leading-tight">
                Aprendé hardware de forma simple
                </h1>
                <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
                  Rendimiento, comparativas, sistemas operativos y usos de tecnologías.
                </p>
              </div>
              <Button
                asChild
                variant="outline"
                className="rounded-md px-5 py-2 self-start bg-white text-black border-black/10 hover:bg-white hover:text-black dark:bg-white dark:text-black dark:border-black/10"
              >
                <Link to="/">Volver al inicio</Link>
              </Button>
            </div>

            <div className="mt-8 flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="flex flex-col sm:flex-row gap-3 sticky top-0 z-10 bg-card/90 backdrop-blur-sm pb-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetFlow}
                  className="rounded-md px-5"
                  disabled={isLoading || !selectedFlow}
                >
                  Reiniciar chat
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBackStep}
                  className="rounded-md px-5"
                  disabled={isLoading || !selectedFlow}
                >
                  Volver a la pregunta anterior
                </Button>
              </div>

              <div className="flex-1 min-h-0 flex flex-col overflow-hidden pr-1">
                <ChatWindow
                  messages={messages}
                  isLoading={isLoading}
                  options={optionChoices}
                  onSelectOption={(option) => {
                    if (!selectedFlow) {
                      handleSelectFlow(option.value);
                      return;
                    }
                    handleOptionSelect(option);
                  }}
                  showCta={isComplete}
                  ctaHref="https://wa.me/5492355544386?text=Hola%20RG%2C%20quiero%20asesoramiento%20sobre%20hardware."
                  ctaLabel="Solicitar asesoramiento por WhatsApp"
                  disabled={isLoading}
                />

                {notice && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    {notice}
                  </div>
                )}
                {error && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}
              </div>

            </div>
          </Card>
        </div>
      </main>
    </PageLayout>
  );
};

export default ChatPage;
